/**
 * 文件夹会员档位表 · 接口4 tiers 驱动 + 硬编码兜底。
 *
 * 接口4（/ai/folder/get/quota）2026-06-08 起返回 `tiers` 全档位有序数组
 * （GroupInit→Vip→VVip→SVip，各带 cap/label）。本模块消费它的 cap（服务端调配额，
 * 前端自动跟随）；档名不取 tiers.label，统一用项目固定文案 VIP_KIND_LABEL
 * （与 privileges 的 VIP_KIND_NAMES 同值），避免 label 带「·无上限」等后缀混进“X会员”主体。
 * tiers 缺失（老后端 / 解析失败）→ cap 回落 §3.1 固定值（与现状一致，不回归）。
 * 不 import config.js（Jest landmine），本地镜像 VIP_KIND。
 */
const VK = { VIP: 1, SVIP: 2, VVIP: 3, EVIP: 4 } as const

/** 接口4 tiers 单项 */
export interface TierInfo {
  /** 后端档位枚举：GroupInit / GroupVip / GroupVVip / GroupSVip */
  kind: string
  /** 本档文件夹配额上限 */
  cap: number
  /** 中文展示文案（服务端预生成） */
  label: string
}

/** vip_kind → 接口4 档位枚举 kind（企业级 EVIP 不在 tiers 内） */
const VK_TO_KIND: Record<number, string> = {
  0: 'GroupInit',
  [VK.VIP]: 'GroupVip',
  [VK.VVIP]: 'GroupVVip',
  [VK.SVIP]: 'GroupSVip'
}

/**
 * 会员档名固定文案 · 档名的唯一来源（不取接口4 tiers.label）。
 * 与 privileges/constants.ts 的 VIP_KIND_NAMES 同源同值；此处本地镜像而非 import，
 * 因为 privileges/constants.ts 会连带引入 config.js 与图标资源，在 Jest 下是 landmine
 * （同上文「不 import config.js」约束）。
 */
const VIP_KIND_LABEL: Record<number, string> = {
  [VK.VIP]: '基础会员',
  [VK.VVIP]: '高级会员',
  [VK.SVIP]: '超级会员',
  [VK.EVIP]: '企业级会员'
}

/** 各档 cap 兜底（§3.1 固定值；非会员 1，企业级无限不在此） */
const FALLBACK_CAP: Record<number, number> = {
  0: 1,
  [VK.VIP]: 20,
  [VK.VVIP]: 300,
  [VK.SVIP]: 2000
}

function findTier(vipKind: number, tiers?: TierInfo[]): TierInfo | undefined {
  const kind = VK_TO_KIND[vipKind]
  if (!kind || !tiers) return undefined
  return tiers.find((t) => t.kind === kind)
}

/** 某档会员档名 · 取项目固定文案 VIP_KIND_LABEL，不跟随接口4 tiers.label */
export function tierLabel(vipKind: number): string {
  return VIP_KIND_LABEL[vipKind] || ''
}

/** 某档文件夹配额 cap：优先 tiers.cap，回落硬编码 */
export function tierCap(vipKind: number, tiers?: TierInfo[]): number {
  const hit = findTier(vipKind, tiers)
  return hit ? hit.cap : (FALLBACK_CAP[vipKind] ?? 0)
}

/**
 * 某档"展示用" cap · 在 tierCap 基础上，把后端的 -1（"无上限"，如超级档 label
 * 「超级会员·无上限」）回落到 §3.1 硬编码配额，用于展示具体数字而非 -1。
 */
export function tierDisplayCap(vipKind: number, tiers?: TierInfo[]): number {
  const cap = tierCap(vipKind, tiers)
  return cap === -1 ? (FALLBACK_CAP[vipKind] ?? 0) : cap
}

/**
 * 顶层 cap 展示口径归一 · 超级 / 企业级团队接口4 的 cap 返回 -1（"无上限"哨兵），
 * 展示统一落到超级档的具体配额（tierDisplayCap：tiers 的 GroupSVip，其也为 -1 时回落 2000）。
 * 其余值原样返回。注意：这里只决定"展示用的数字"，是否真正限制由各处卡点逻辑单独决定。
 */
export function resolveDisplayCap(cap: number, tiers?: TierInfo[]): number {
  return cap === -1 ? tierDisplayCap(VK.SVIP, tiers) : cap
}

/**
 * 开通弹窗 tab 小字各档配额文案（emphasis 段）· 企业级恒「更多」（不在 tiers 内）。
 * 用 tierDisplayCap：服务端调某档 cap 自动跟随；超级档 cap=-1（"无上限"）时展示回落 2000，
 * 与常驻入口 widget 口径一致，不显示 -1。
 */
export function tierTabNotes(tiers?: TierInfo[]): {
  basic: string
  advanced: string
  super: string
  enterprise: string
} {
  return {
    basic: `${tierDisplayCap(VK.VIP, tiers)}个`,
    advanced: `${tierDisplayCap(VK.VVIP, tiers)}个`,
    super: `${tierDisplayCap(VK.SVIP, tiers)}个`,
    enterprise: '更多'
  }
}
